import { format } from 'date-fns';

export const getDayDateKey = (date) => format(date, 'yyyy-MM-dd');

export const getDayDroppableId = (dateKey) => `day-${dateKey}`;

export const parseDayDroppableId = (droppableId) => {
  if (typeof droppableId !== 'string' || !droppableId.startsWith('day-')) {
    return null;
  }
  return droppableId.slice(4);
};

export const parseDateTimeString = (dateTimeStr) => {
  if (!dateTimeStr) {
    return { datePart: null, timePart: '09:00:00' };
  }

  const value = dateTimeStr.toString();
  if (value.includes('T')) {
    const [datePart, timePartRaw] = value.split('T');
    const timePart = (timePartRaw || '09:00:00').split('.')[0];
    return { datePart, timePart };
  }

  const [datePart, timePart = '09:00:00'] = value.split(' ');
  return { datePart, timePart };
};

export const combineDateAndTime = (datePart, timePart) => `${datePart} ${timePart}`;

export const setDateOnDateTime = (dateTimeStr, newDatePart) => {
  const { timePart } = parseDateTimeString(dateTimeStr);
  return combineDateAndTime(newDatePart, timePart);
};

export const parseLocalMs = (dateTimeStr) => {
  const { datePart, timePart } = parseDateTimeString(dateTimeStr);
  if (!datePart) {
    return 0;
  }

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
};

export const isDraggableItineraryItem = (item) => {
  if (!item || item._isRoadTripCard || item.consolidated) {
    return false;
  }

  if (!item.start_datetime) {
    return false;
  }

  if (item.end_datetime) {
    const start = parseDateTimeString(item.start_datetime);
    const end = parseDateTimeString(item.end_datetime);
    if (start.datePart !== end.datePart) {
      return false;
    }
  }

  return true;
};

export const recalculateDayItemTimes = (orderedItems, dayDateKey) => {
  if (!orderedItems.length) {
    return [];
  }

  const slots = orderedItems
    .filter((item) => item.start_datetime)
    .map((item) => ({
      start: setDateOnDateTime(item.start_datetime, dayDateKey),
      end: item.end_datetime
        ? setDateOnDateTime(item.end_datetime, dayDateKey)
        : setDateOnDateTime(item.start_datetime, dayDateKey),
    }))
    .sort((a, b) => parseLocalMs(a.start) - parseLocalMs(b.start));

  const timedItems = orderedItems.filter((item) => item.start_datetime);

  return timedItems.map((item, index) => ({
    id: item.id,
    start_datetime: slots[index]?.start || setDateOnDateTime(item.start_datetime, dayDateKey),
    end_datetime: slots[index]?.end || (item.end_datetime
      ? setDateOnDateTime(item.end_datetime, dayDateKey)
      : setDateOnDateTime(item.start_datetime, dayDateKey)),
  }));
};

export const buildDragReorderUpdates = ({
  days,
  activeItemId,
  sourceDayKey,
  targetDayKey,
  targetIndex,
}) => {
  const dayMap = new Map(
    days.map((day) => [getDayDateKey(day.date), [...day.items]])
  );

  const sourceItems = dayMap.get(sourceDayKey) || [];
  const activeIndex = sourceItems.findIndex((item) => item.id === activeItemId);
  if (activeIndex === -1) {
    return [];
  }

  const [movedItem] = sourceItems.splice(activeIndex, 1);
  dayMap.set(sourceDayKey, sourceItems);

  const targetItems = sourceDayKey === targetDayKey
    ? sourceItems
    : [...(dayMap.get(targetDayKey) || [])];

  const clampedIndex = Math.max(0, Math.min(targetIndex, targetItems.length));
  targetItems.splice(clampedIndex, 0, movedItem);
  dayMap.set(targetDayKey, targetItems);

  const affectedDayKeys = sourceDayKey === targetDayKey
    ? [sourceDayKey]
    : [sourceDayKey, targetDayKey];

  const updates = [];
  const seenIds = new Set();

  affectedDayKeys.forEach((dayKey) => {
    const items = dayMap.get(dayKey) || [];
    const recalculated = recalculateDayItemTimes(items, dayKey);

    recalculated.forEach((update) => {
      if (seenIds.has(update.id)) {
        return;
      }

      seenIds.add(update.id);
      const original = items.find((item) => item.id === update.id);
      if (!original) {
        return;
      }

      const startChanged = original.start_datetime !== update.start_datetime;
      const endChanged = (original.end_datetime || null) !== (update.end_datetime || null);
      if (startChanged || endChanged) {
        updates.push(update);
      }
    });
  });

  return updates;
};

export const resolveDragTarget = ({ days, activeItemId, overId }) => {
  if (overId == null) {
    return null;
  }

  const overIdStr = String(overId);
  const dayKeyFromDroppable = parseDayDroppableId(overIdStr);
  if (dayKeyFromDroppable) {
    const day = days.find((entry) => getDayDateKey(entry.date) === dayKeyFromDroppable);
    if (!day) {
      return null;
    }

    return {
      sourceDayKey: null,
      targetDayKey: dayKeyFromDroppable,
      targetIndex: day.items.length,
    };
  }

  let sourceDayKey = null;
  let sourceIndex = -1;

  days.forEach((day) => {
    const dayKey = getDayDateKey(day.date);
    const index = day.items.findIndex((item) => item.id === activeItemId);
    if (index !== -1) {
      sourceDayKey = dayKey;
      sourceIndex = index;
    }
  });

  if (!sourceDayKey) {
    return null;
  }

  let targetDayKey = null;
  let targetIndex = -1;

  days.forEach((day) => {
    const dayKey = getDayDateKey(day.date);
    const index = day.items.findIndex((item) => item.id === overId);
    if (index !== -1) {
      targetDayKey = dayKey;
      targetIndex = index;
    }
  });

  if (!targetDayKey) {
    return null;
  }

  if (sourceDayKey === targetDayKey && sourceIndex < targetIndex) {
    targetIndex -= 1;
  }

  return { sourceDayKey, targetDayKey, targetIndex };
};
